import * as React from 'react'
import { useMutation } from '@apollo/react-hooks'
import dynamic from 'next/dynamic'

import {
  i18n,
  LiveMessage,
  Routes,
  useFormattableI18nProperty,
  useLogger,
  useLoginCheck,
  useSiteConfig,
  useSiteDefinition,
} from '@thg-commerce/enterprise-core'
import { getSafeUrlParametersFromWindow } from '@thg-commerce/enterprise-core/src/urlUtilities/urlUtilities'
import {
  EnhancedTrackingEvent,
  pushToEnhancedDataLayer,
} from '@thg-commerce/enterprise-metrics/src/data_layer'
import { ReportReviewResult } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Mutation/ReportReview'
import { VoteReviewNegativeResult } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Mutation/VoteReviewNegative'
import { VoteReviewPositiveResult } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Mutation/VoteReviewPositive'
import {
  MutationReportReviewArgs,
  MutationVoteReviewNegativeArgs,
  MutationVoteReviewPositiveArgs,
  ReviewActionResult,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import { ReviewWithFeatures } from '@thg-commerce/enterprise-network/src/transformers/product/reviews'
import { useTheme } from '@thg-commerce/enterprise-pages/src/Product/theme'
import {
  FeatureRatingDisplay,
  ProductReview,
} from '@thg-commerce/gravity-elements'
import { PageMessage } from '@thg-commerce/gravity-patterns'
import { spacing } from '@thg-commerce/gravity-theme'

import { ReportReview as REPORT_REVIEW_MUTATION } from '../../graphql/ReportReview.graphql'
import { VoteReviewNegative as VOTE_REVIEW_POSITIVE_MUTATION } from '../../graphql/VoteReviewNegative.graphql'
import { VoteReviewPositive as VOTE_REVIEW_NEGATIVE_MUTATION } from '../../graphql/VoteReviewPositive.graphql'
import { REVIEW_CARD_HEIGHT } from '../ProductReviews/TopProductReviews/TopProductReviews'

import {
  ButtonsWrapper,
  CustomerReviewSingle,
  FeatureRating,
  PlatformMessageContainer,
  ReviewExcerpt,
  ReviewFooter,
  ReviewFooterDateAndName,
  ReviewFooterVerified,
  ReviewRating,
  ReviewReport,
  ReviewTitle,
  ReviewVotingButtonsContainer,
  VoteIcon,
  VoteNoButton,
  VoteYesButton,
  VotingArea,
  VotingAreaQuestion,
} from './styles'

const SvgLike = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/Like'),
)
const SvgDislike = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/Dislike'),
)

const SvgIcon = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/SvgIcon'),
  { ssr: true, loading: () => <div style={{ width: 24, height: 24 }} /> },
)

type ProductReviewScoreAndContent = {
  score: number
  content: string
  maxScore?: number
}

type ProductReviewContentProps = {
  productTitle: string
  customerReview: ReviewWithFeatures
  sku: number
  inViewport?: boolean
  forwardedRef?: React.Ref<any>
  enterCount?: number
  showInlineFeatureScores?: boolean
}

export enum ReviewVoteActions {
  NONE = 'NONE',
  POSITIVE = 'POSITIVE',
  NEGATIVE = 'NEGATIVE',
  REPORT = 'REPORT',
  DUPLICATE_REPORT = 'DUPLICATE_REPORT',
  DUPLICATE_VOTE = 'DUPLICATE_VOTE',
}

const dataTestIdMap = {
  POSITIVE: 'vote-registered-message',
  NEGATIVE: 'vote-registered-message',
  DUPLICATE_VOTE: 'vote-duplicated-message',
  REPORT: 'review-reported-message',
  DUPLICATE_REPORT: 'report-review-duplicated-message',
}

export const ProductReviewContent = ({
  productTitle,
  customerReview,
  sku,
  inViewport,
  forwardedRef,
  enterCount,
  showInlineFeatureScores,
}: ProductReviewContentProps) => {
  const isLoggedIn = useLoginCheck()
  const logger = useLogger()
  const { enableReviewFormFeatureRatings } = useSiteConfig()

  const { defaultLocale } = useSiteDefinition()
  const theme = useTheme()

  const [
    disableScrollToPageMessage,
    setDisableScrollToPageMessage,
  ] = React.useState(false)

  const [totalPositiveVotes, setTotalPositiveVotes] = React.useState(
    customerReview.positiveVotes || 0,
  )

  const [totalNegativeVotes, setTotalNegativeVotes] = React.useState(
    customerReview.negativeVotes || 0,
  )

  const [reviewVoteActionResult, setReviewVoteActionResult] = React.useState<
    ReviewVoteActions
  >(ReviewVoteActions.NONE)

  // This is added to determine once the component renders on client side
  // We use it as a key on the buttons, so that it will re-render with
  // href value it need which requires access to window object
  const [clientSideRender, setClientSideRender] = React.useState(false)

  const i18nText = {
    verified: i18n('review.content.verifiedpurchase'),
    wasHelpful: i18n('review.content.washelpful.text'),
    yesButtonText: i18n('review.content.positive.text'),
    noButtonText: i18n('review.content.negative.text'),
    reportReview: i18n('review.content.report.text'),
    reviewDateAndNameBy: i18n('review.content.by.text'),
    voteRegistered: i18n('review.content.voteregistered'),
    voteDuplicated: i18n('review.content.duplicatevote'),
    reviewReported: i18n('review.content.reviewreported'),
    reportReviewDuplicated: i18n('review.content.duplicate.reviewreported'),
    yesButtonLabel: useFormattableI18nProperty('review.content.positive.label'),
    noButtonLabel: useFormattableI18nProperty('review.content.negative.label'),
    starRatingLabel: useFormattableI18nProperty(
      'review.content.starrating.label',
    ),
    reviewsCounterSuffix: i18n('reviews.reviewscountersuffix.text'),
  }

  const reviewTitle = customerReview.title || productTitle || ''
  const customerReviewId = customerReview.id
  const reviewDate = new Date(customerReview.posted).toLocaleDateString(
    defaultLocale?.replace('_', '-') || 'en-GB',
    {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    },
  )

  interface ReviewVotedTrackingEvent extends EnhancedTrackingEvent {
    component: string
    voted: string
    id: string
  }

  const [voteYesOnReview] = useMutation<
    {
      voteReviewPositive: VoteReviewPositiveResult
    },
    MutationVoteReviewPositiveArgs
  >(VOTE_REVIEW_NEGATIVE_MUTATION, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      const positiveReviewData = data.voteReviewPositive.voteReviewPositive

      if (positiveReviewData === ReviewActionResult.Successful) {
        setReviewVoteActionResult(ReviewVoteActions.POSITIVE)
        setTotalPositiveVotes((previousVotes) => {
          return previousVotes + 1
        })
        pushToEnhancedDataLayer<ReviewVotedTrackingEvent>({
          event: 'review_voted',
          component: 'ProductReviews component',
          voted: 'yes',
          id: customerReviewId,
        })
      }

      if (positiveReviewData === ReviewActionResult.AlreadyVoted) {
        setReviewVoteActionResult(ReviewVoteActions.DUPLICATE_VOTE)
      }
    },
    onError: (error) => {
      logger.error(
        `[HelpfulReview]: Failed to mark review as helpful with error ${error.message}`,
      )
    },
  })

  const [voteNoOnReview] = useMutation<
    {
      voteReviewNegative: VoteReviewNegativeResult
    },
    MutationVoteReviewNegativeArgs
  >(VOTE_REVIEW_POSITIVE_MUTATION, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      const negativeReviewData = data.voteReviewNegative.voteReviewNegative

      if (negativeReviewData === ReviewActionResult.Successful) {
        setReviewVoteActionResult(ReviewVoteActions.NEGATIVE)
        setTotalNegativeVotes((previousVotes) => {
          return previousVotes + 1
        })
        pushToEnhancedDataLayer<ReviewVotedTrackingEvent>({
          event: 'review_voted',
          component: 'ProductReviews component',
          voted: 'no',
          id: customerReviewId,
        })
      }

      if (negativeReviewData === ReviewActionResult.AlreadyVoted) {
        setReviewVoteActionResult(ReviewVoteActions.DUPLICATE_VOTE)
      }
    },
    onError: (error) => {
      logger.error(
        `[UnhelpfulReview]: Failed to mark review as unhelpful with error ${error.message}`,
      )
    },
  })

  const [reportTheReview] = useMutation<
    { reportReview: ReportReviewResult },
    MutationReportReviewArgs
  >(REPORT_REVIEW_MUTATION, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      const reportedReviewData = data.reportReview.reportReview

      if (reportedReviewData === ReviewActionResult.Successful) {
        setReviewVoteActionResult(ReviewVoteActions.REPORT)
      }

      if (reportedReviewData === ReviewActionResult.AlreadyVoted) {
        setReviewVoteActionResult(ReviewVoteActions.DUPLICATE_REPORT)
      }
    },
    onError: (error) => {
      logger.error(
        `[ReportReview]: Failed to report review with error ${error.message}`,
      )
    },
  })

  const performVotingAction = React.useCallback(
    (reviewAction: ReviewVoteActions, disableScrollToPageMessage?: boolean) => {
      if (!sku) {
        return
      }

      setDisableScrollToPageMessage(!!disableScrollToPageMessage)

      switch (reviewAction) {
        case ReviewVoteActions.POSITIVE:
          return voteYesOnReview({
            variables: {
              input: {
                sku,
                reviewId: customerReviewId,
              },
            },
          })
        case ReviewVoteActions.NEGATIVE:
          return voteNoOnReview({
            variables: {
              input: {
                sku,
                reviewId: customerReviewId,
              },
            },
          })
        case ReviewVoteActions.REPORT:
          return reportTheReview({
            variables: {
              input: {
                sku,
                reviewId: customerReviewId,
              },
            },
          })
        default:
          return undefined
      }
    },
    [reportTheReview, customerReviewId, voteNoOnReview, voteYesOnReview, sku],
  )

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const { reviewAction, reviewId } = getSafeUrlParametersFromWindow<{
        reviewAction: string
        reviewId: string
      }>(window, ['reviewAction', 'reviewId'])

      if (isLoggedIn && reviewAction && reviewId === customerReviewId) {
        // Once read from url, these are no longer required
        // Used to bring notice to customer that we've updated their review (action)
        const url = new URL(window.location.href)
        url.searchParams.delete('reviewAction')
        url.searchParams.delete('reviewId')
        window.history.replaceState({}, document.title, url.toString())

        switch (reviewAction) {
          case ReviewVoteActions.POSITIVE:
            performVotingAction(ReviewVoteActions.POSITIVE, true)
            break
          case ReviewVoteActions.NEGATIVE:
            performVotingAction(ReviewVoteActions.NEGATIVE, true)
            break
          case ReviewVoteActions.REPORT:
            performVotingAction(ReviewVoteActions.REPORT, true)
            break
        }
      }
    }
  }, [isLoggedIn, customerReviewId, performVotingAction])

  const reviewElements: ProductReviewScoreAndContent = customerReview.elements?.reduce(
    (returnElement, currentElement) => {
      if (currentElement.key === 'score' && currentElement.score) {
        returnElement['score'] = currentElement.score
      }

      if (currentElement.key === 'content' && currentElement.value) {
        returnElement['content'] = currentElement.value
      }

      return returnElement
    },
    {
      score: 0,
      content: '',
    },
  )

  const returnToUrl = (reviewAction: ReviewVoteActions) => {
    if (typeof window !== 'undefined') {
      const currentUrl = new URL(window.location.href)
      currentUrl.searchParams.set('reviewAction', reviewAction)
      currentUrl.searchParams.set('reviewId', customerReviewId)

      return `${Routes.Login}?returnTo=${encodeURIComponent(`${currentUrl}`)}`
    }
    return undefined
  }

  const i18nTextMap = {
    POSITIVE: i18nText.voteRegistered,
    NEGATIVE: i18nText.voteRegistered,
    DUPLICATE_VOTE: i18nText.voteDuplicated,
    REPORT: i18nText.reviewReported,
    DUPLICATE_REPORT: i18nText.reportReviewDuplicated,
  }

  const ref = React.createRef<HTMLDivElement>()

  React.useEffect(() => {
    setClientSideRender(true)
  }, [])

  const reviewSectionColSpan = showInlineFeatureScores ? [12, 12, 8, 8] : 12

  const ReviewContent = (props: { ref?: React.Ref<any> }) => (
    <div key={customerReview.id} ref={props?.ref}>
      <CustomerReviewSingle
        margin={theme.patterns.productBlock.components.productReview.margin}
        paddingTop={
          theme.pageTheme.topProductReviews.individualReviewPaddingTop
        }
      >
        {reviewTitle && (
          <ReviewTitle
            colSpan={reviewSectionColSpan}
            colStart={1}
            showInlineFeatureScores={showInlineFeatureScores}
            textStyle={theme.pageTheme.productReviewContent.title?.textStyle}
          >
            {reviewTitle}
          </ReviewTitle>
        )}

        <ReviewRating
          colSpan={reviewSectionColSpan}
          colStart={1}
          showInlineFeatureScores={showInlineFeatureScores}
          data-testid="review-rating"
        >
          <ProductReview
            starRating={reviewElements.score || 0}
            screenReaderOnlyText={i18nText.starRatingLabel(
              reviewElements.score?.toString() || '0',
            )}
            reviewsCounterSuffixText={i18nText.reviewsCounterSuffix}
            colorFills={theme.pageTheme?.productReviewContent.star.colorFills}
          />
        </ReviewRating>
        {reviewElements?.content && (
          <ReviewExcerpt
            colSpan={[12, 12, 8, 8]}
            colStart={1}
            showInlineFeatureScores={showInlineFeatureScores}
            data-testid="review-excerpt"
          >
            {reviewElements.content}
          </ReviewExcerpt>
        )}
        {enableReviewFormFeatureRatings && (
          <FeatureRating
            colSpan={showInlineFeatureScores ? [12, 12, 4, 4] : 12}
            colStart={showInlineFeatureScores ? [1, 1, 9, 9] : 1}
            rowStart={showInlineFeatureScores ? [4, 4, 1, 1] : 4}
            rowSpan={showInlineFeatureScores ? [1, 1, 6, 6] : 1}
            showInlineFeatureScores={showInlineFeatureScores}
          >
            <FeatureRatingDisplay featureRatings={customerReview.elements} />
          </FeatureRating>
        )}
        <ReviewFooter
          colSpan={reviewSectionColSpan}
          colStart={1}
          showInlineFeatureScores={showInlineFeatureScores}
        >
          <ReviewFooterDateAndName
            textStyle={theme.pageTheme.productReviewContent.footer?.textStyle}
          >
            <time dateTime={customerReview.posted}>{`${reviewDate} `}</time>
            <span>
              {i18nText.reviewDateAndNameBy} {customerReview.authorName}
            </span>
          </ReviewFooterDateAndName>
          {customerReview.verifiedPurchase && (
            <ReviewFooterVerified
              textStyle={theme.pageTheme.productReviewContent.footer?.textStyle}
            >
              {i18nText.verified}
            </ReviewFooterVerified>
          )}
        </ReviewFooter>

        <VotingArea
          colSpan={reviewSectionColSpan}
          colStart={1}
          showInlineFeatureScores={showInlineFeatureScores}
        >
          <VotingAreaQuestion
            ref={ref}
            textStyle={
              theme.pageTheme.productReviewContent?.votingAreaQuestion
                ?.textStyle
            }
          >
            {i18nText.wasHelpful}
          </VotingAreaQuestion>
          <div style={{ display: 'inline' }}>
            <ButtonsWrapper>
              <VoteYesButton
                key={`vote-yes-button-client-${clientSideRender.toString()}`}
                emphasis="medium"
                aria-label={i18nText.yesButtonLabel(
                  totalPositiveVotes.toString(),
                )}
                data-testid="vote-yes-button"
                onClick={
                  isLoggedIn
                    ? () => performVotingAction(ReviewVoteActions.POSITIVE)
                    : undefined
                }
                href={
                  isLoggedIn
                    ? undefined
                    : returnToUrl(ReviewVoteActions.POSITIVE)
                }
                renderedAs={isLoggedIn ? undefined : 'a'}
              >
                <VoteIcon>
                  {theme.pageTheme?.productReviewContent?.thumbsIcon?.like
                    ?.svgPath ? (
                    <SvgIcon
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox={
                        theme.pageTheme.productReviewContent.thumbsIcon.viewBox
                      }
                      width={
                        theme.pageTheme.productReviewContent.thumbsIcon.width
                      }
                      height={
                        theme.pageTheme.productReviewContent.thumbsIcon.height
                      }
                    >
                      <path
                        d={
                          theme.pageTheme.productReviewContent.thumbsIcon.like
                            .svgPath
                        }
                        fillRule="evenodd"
                      />
                    </SvgIcon>
                  ) : (
                    <SvgLike />
                  )}
                </VoteIcon>
                {i18nText.yesButtonText} ({totalPositiveVotes})
              </VoteYesButton>
              <VoteNoButton
                key={`vote-no-button-client-${clientSideRender.toString()}`}
                emphasis="medium"
                aria-label={i18nText.noButtonLabel(
                  totalNegativeVotes.toString(),
                )}
                data-testid="vote-no-button"
                onClick={
                  isLoggedIn
                    ? () => performVotingAction(ReviewVoteActions.NEGATIVE)
                    : undefined
                }
                href={
                  isLoggedIn
                    ? undefined
                    : returnToUrl(ReviewVoteActions.NEGATIVE)
                }
                renderedAs={isLoggedIn ? undefined : 'a'}
              >
                <VoteIcon>
                  {theme.pageTheme?.productReviewContent?.thumbsIcon?.dislike
                    ?.svgPath ? (
                    <SvgIcon
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox={
                        theme.pageTheme.productReviewContent.thumbsIcon.viewBox
                      }
                      width={
                        theme.pageTheme.productReviewContent.thumbsIcon.width
                      }
                      height={
                        theme.pageTheme.productReviewContent.thumbsIcon.height
                      }
                    >
                      <path
                        d={
                          theme.pageTheme.productReviewContent.thumbsIcon
                            .dislike.svgPath
                        }
                        fillRule="evenodd"
                      />
                    </SvgIcon>
                  ) : (
                    <SvgDislike />
                  )}
                </VoteIcon>
                {i18nText.noButtonText} ({totalNegativeVotes})
              </VoteNoButton>
            </ButtonsWrapper>
            <PlatformMessageContainer
              showMessage={reviewVoteActionResult !== ReviewVoteActions.NONE}
            >
              <PageMessage
                focusRef={ref}
                flashMessageId={`review-result-${customerReview.id}`}
                type={
                  reviewVoteActionResult === ReviewVoteActions.POSITIVE ||
                  reviewVoteActionResult === ReviewVoteActions.NEGATIVE ||
                  reviewVoteActionResult === ReviewVoteActions.REPORT
                    ? 'success'
                    : 'info'
                }
                message={i18nTextMap[reviewVoteActionResult]}
                data-testid={dataTestIdMap[reviewVoteActionResult]}
                renderAnnouncer={() => (
                  <LiveMessage
                    ariaLive="polite"
                    message={i18nTextMap[reviewVoteActionResult]}
                  />
                )}
                disableScroll={!disableScrollToPageMessage}
              />
            </PlatformMessageContainer>
          </div>
        </VotingArea>
        <ReviewVotingButtonsContainer
          colSpan={reviewSectionColSpan}
          colStart={1}
          showInlineFeatureScores={showInlineFeatureScores}
        >
          <ReviewReport
            key={`report-button-client-${clientSideRender.toString()}`}
            sizing="regular"
            emphasis="low"
            data-testid="report-review-button"
            style={{ padding: `${spacing(1.5)} 0` }}
            onClick={
              isLoggedIn
                ? () => performVotingAction(ReviewVoteActions.REPORT)
                : undefined
            }
            href={
              isLoggedIn ? undefined : returnToUrl(ReviewVoteActions.REPORT)
            }
            renderedAs={isLoggedIn ? undefined : 'a'}
            textStyle={theme.pageTheme.productReviewContent.report?.textStyle}
          >
            {i18nText.reportReview}
          </ReviewReport>
        </ReviewVotingButtonsContainer>
      </CustomerReviewSingle>
    </div>
  )

  return !forwardedRef ? (
    <ReviewContent />
  ) : (enterCount && enterCount > 0) || inViewport ? (
    <ReviewContent ref={forwardedRef} />
  ) : (
    <div
      style={{
        visibility: 'hidden',
        marginBottom: `${REVIEW_CARD_HEIGHT}px`,
        marginRight: '100%',
      }}
      ref={forwardedRef}
    />
  )
}
